/**
 * Statistics Service
 * 통계 데이터 가공 및 분석 비즈니스 로직
 */

import { StatisticsRepository } from '../repositories';
import {
  DashboardStats,
  RevenueStats,
  OrganizationStats,
  ServiceType,
} from '../models';
import { formatDateKST } from '../../../utils/dateUtils';

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string;
    borderColor?: string;
  }[];
}

export class StatisticsService {
  constructor(private repository: StatisticsRepository) {}

  /**
   * 대시보드 통계 조회
   */
  async getDashboardStats(serviceType?: ServiceType): Promise<DashboardStats> {
    // Repository는 serviceType 파라미터를 지원하지 않으므로 무시
    return await this.repository.getDashboardStats();
  }

  /**
   * 매출 통계 조회
   */
  async getRevenueStats(
    startDate?: string,
    endDate?: string,
    serviceType?: ServiceType
  ): Promise<RevenueStats> {
    // Repository는 파라미터를 지원하지 않으므로 무시
    return await this.repository.getRevenueStats();
  }

  /**
   * 사업자 통계 조회
   */
  async getOrganizationStats(
    startDate?: string,
    endDate?: string,
    serviceType?: ServiceType
  ): Promise<OrganizationStats> {
    // Repository는 파라미터를 지원하지 않으므로 무시
    return await this.repository.getOrganizationStats();
  }

  /**
   * 대시보드 요약 정보 생성
   */
  generateDashboardSummary(stats: DashboardStats): {
    pendingActions: number;
    activeServices: number;
    monthlyRevenue: string;
    todayActivity: number;
  } {
    return {
      pendingActions:
        stats.pending_organizations + stats.pending_contacts,
      activeServices: stats.active_subscriptions,
      monthlyRevenue: this.formatCurrency(stats.monthly_revenue),
      todayActivity: stats.today_logins + stats.today_actions,
    };
  }

  /**
   * 매출 차트 데이터 생성
   */
  generateRevenueChart(stats: RevenueStats): ChartData {
    return {
      labels: ['지난달', '이번달'],
      datasets: [
        {
          label: '월별 매출',
          data: [stats.last_month_revenue, stats.current_month_revenue],
          backgroundColor: 'rgba(59, 130, 246, 0.5)',
          borderColor: 'rgb(59, 130, 246)',
        },
      ],
    };
  }

  /**
   * 사업자 가입 추이 차트 데이터 생성
   */
  generateOrganizationTrendChart(stats: OrganizationStats): ChartData {
    return {
      labels: ['오늘', '이번주', '이번달'],
      datasets: [
        {
          label: '신규 가입',
          data: [stats.today_signups, stats.this_week_signups, stats.this_month_signups],
          backgroundColor: 'rgba(34, 197, 94, 0.5)',
          borderColor: 'rgb(34, 197, 94)',
        },
      ],
    };
  }

  /**
   * 승인율 계산
   */
  calculateApprovalRate(stats: DashboardStats): number {
    const total =
      stats.approved_organizations +
      stats.rejected_organizations +
      stats.pending_organizations;

    if (total === 0) return 0;

    return Math.round((stats.approved_organizations / total) * 100);
  }

  /**
   * 완료율 계산 (문의)
   */
  calculateCompletionRate(stats: DashboardStats): number {
    const total = stats.total_contacts;
    if (total === 0) return 0;

    return Math.round((stats.completed_contacts / total) * 100);
  }

  /**
   * 활성 구독률 계산
   */
  calculateActiveSubscriptionRate(stats: DashboardStats): number {
    const total = stats.total_subscriptions;
    if (total === 0) return 0;

    return Math.round((stats.active_subscriptions / total) * 100);
  }

  /**
   * 월별 매출 성장률 계산
   */
  calculateRevenueGrowthRate(stats: RevenueStats): number {
    return stats.revenue_growth_rate;
  }

  /**
   * 평균 구독 금액 계산
   */
  calculateAverageSubscriptionPrice(stats: RevenueStats): number {
    return stats.average_monthly_fee;
  }

  /**
   * 서비스별 매출 분포
   */
  getServiceRevenueDistribution(
    stats: RevenueStats
  ): Record<ServiceType, number> {
    return {
      studyroom: stats.studyroom_revenue,
      academy: stats.academy_revenue,
      cafe: 0,
      gym: 0,
    };
  }

  /**
   * 통화 형식 포맷팅
   */
  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
    }).format(amount);
  }

  /**
   * 날짜 범위 검증
   */
  validateDateRange(startDate?: string, endDate?: string): boolean {
    if (!startDate || !endDate) return true;

    const start = new Date(startDate);
    const end = new Date(endDate);

    return start <= end;
  }

  /**
   * 기본 날짜 범위 생성 (최근 30일)
   */
  getDefaultDateRange(): DateRange {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    return {
      startDate: formatDateKST(startDate),
      endDate: formatDateKST(endDate),
    };
  }
}
